import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactConfetti from 'react-confetti';
import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import { CircleCheck } from '@parrot-software/design-system/icons';

import { CASH_REGISTER_TYPES, TAX_RATES, WIZARD_STEPS } from '@onboarding/domain/value-objects';
import { getWizardStepIndex } from '@onboarding/presentation/utils';

type CompletionSummary = {
  restaurantType: string;
  tipEnabled: boolean;
  tipPercentages: number[];
  cashRegisterType: string;
  taxRate: string;
  receiptConfigured: boolean;
};

type CompletionStepProps = {
  errorMessage?: string;
  isFinishing?: boolean;
  onEdit: (stepIndex: number) => void;
  onFinish: () => void;
  summary: CompletionSummary;
};

type SummaryItemProps = {
  label: string;
  onEdit?: () => void;
  value: string;
};

/**
 * Renders a single summary row: icon + label + bold value + optional edit button.
 */
const SummaryItem = ({ label, onEdit, value }: SummaryItemProps) => {
  const { t } = useTranslation('onboarding');

  return (
    <div className="flex items-start gap-2">
      <CircleCheck className="shrink-0 text-(--feedback-success)" size={24} />
      <p className="flex-1 text-sm leading-5 text-foreground">
        <span>{label} </span>
        <span className="block font-semibold md:inline">{value}</span>
      </p>
      {onEdit && (
        <Button className="shrink-0 text-primary" onClick={onEdit} type="button" variant="link">
          {t('steps.completion.editButton')}
        </Button>
      )}
    </div>
  );
};

/**
 * Completion screen — dynamic summary of all wizard answers with edit buttons.
 */
export const CompletionStep = ({
  errorMessage,
  isFinishing = false,
  onEdit,
  onFinish,
  summary,
}: CompletionStepProps) => {
  const { t } = useTranslation('onboarding');
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  const restaurantLabel = t(`steps.restaurantType.options.${summary.restaurantType}`);

  const cashRegisterValue =
    summary.cashRegisterType === CASH_REGISTER_TYPES.BLIND
      ? t('steps.completion.summary.cashRegisterBlind')
      : t('steps.completion.summary.cashRegisterVisible');

  const formatPercentages = (values: number[]): string => {
    const formatted = values.map((v) => `${Math.round(v * 100)}%`);
    if (formatted.length <= 1) return formatted.join('');
    return `${formatted.slice(0, -1).join(', ')} y ${formatted[formatted.length - 1]}`;
  };

  const tipValue = summary.tipEnabled
    ? `${t('steps.completion.summary.tipEnabled')} ${t('steps.completion.summary.tipSuggestions', {
        percentages: formatPercentages(summary.tipPercentages),
      })}`
    : t('steps.completion.summary.tipDisabled');

  const taxValueMap: Record<string, string> = {
    [TAX_RATES.STANDARD]: t('steps.completion.summary.taxStandard'),
    [TAX_RATES.BORDER]: t('steps.completion.summary.taxBorder'),
  };
  const taxValue = taxValueMap[summary.taxRate] ?? t('steps.completion.summary.taxStandard');

  const receiptValue = summary.receiptConfigured
    ? t('steps.completion.summary.receiptConfigured')
    : t('steps.completion.summary.receiptPending');

  return (
    <div className="flex flex-1 flex-col gap-7 py-3">
      {showConfetti &&
        createPortal(
          <ReactConfetti
            height={window.innerHeight}
            numberOfPieces={500}
            recycle={false}
            style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999, pointerEvents: 'none' }}
            width={window.innerWidth}
          />,
          document.body,
        )}
      <div className="flex flex-col gap-1 text-center">
        <h2 className="text-xl font-semibold leading-8 text-foreground">{t('steps.completion.title')}</h2>
        <p className="text-sm leading-5 text-muted-foreground">{t('steps.completion.subtitle1')}</p>
        <p className="text-sm leading-5 text-muted-foreground">{t('steps.completion.subtitle2')}</p>
      </div>

      <div className="flex flex-col gap-3 py-3">
        <SummaryItem
          label={t('steps.completion.summary.restaurantTypeLabel')}
          onEdit={() => onEdit(getWizardStepIndex(WIZARD_STEPS.RESTAURANT_TYPE))}
          value={restaurantLabel}
        />
        <SummaryItem
          label={t('steps.completion.summary.tipLabel')}
          onEdit={() => onEdit(getWizardStepIndex(WIZARD_STEPS.TIP))}
          value={tipValue}
        />
        <SummaryItem
          label={t('steps.completion.summary.cashRegisterLabel')}
          onEdit={() => onEdit(getWizardStepIndex(WIZARD_STEPS.CASH_REGISTER))}
          value={cashRegisterValue}
        />
        <SummaryItem
          label={t('steps.completion.summary.taxLabel')}
          onEdit={() => onEdit(getWizardStepIndex(WIZARD_STEPS.TAX))}
          value={taxValue}
        />
        <SummaryItem
          label={t('steps.completion.summary.receiptLabel')}
          onEdit={() => onEdit(getWizardStepIndex(WIZARD_STEPS.RECEIPT))}
          value={receiptValue}
        />
      </div>

      <div className="mt-auto flex flex-col items-stretch gap-2 pt-4 lg:items-center">
        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
        <Button className="w-full lg:w-auto" disabled={isFinishing} onClick={onFinish} type="button">
          {isFinishing ? t('steps.completion.finishingButton') : t('steps.completion.finishButton')}
        </Button>
      </div>
    </div>
  );
};
